import { defineStore } from "pinia";
import { OFFER_STATUS_ACCEPTED } from "@price-rescue/domain/statuses";

import type {
  AssignmentDraft,
  DecisionDraft,
  PriceMatchRequestDetail,
  PriceMatchRequestRow,
  GuestRequestCustomerCandidates,
  RequestDecisionAction,
  RequestQueueFilters,
  RequestQueueSummary,
} from "./models.js";
import type { MerchantRequestsService } from "./service.js";

export const useRequestsStore = defineStore("merchant-requests", {
  state: (): {
    assignmentDrafts: Record<string, AssignmentDraft>;
    assignmentError?: string;
    attachmentError?: string;
    conversationError?: string;
    customerNoteError?: string;
    customerLinkError?: string;
    customerLinkCandidates?: GuestRequestCustomerCandidates;
    customerLinkMessage?: string;
    loadingCustomerLinkCandidates: boolean;
    linkingCustomerId?: string;
    decisionDrafts: Record<string, DecisionDraft>;
    detail?: PriceMatchRequestDetail;
    detailError?: string;
    filters: RequestQueueFilters;
    internalNoteError?: string;
    items: PriceMatchRequestRow[];
    listError?: string;
    loadingDetail: boolean;
    loadingList: boolean;
    escalatingOverdue: boolean;
    mutationError?: string;
    refreshingEvidence: boolean;
    resendingCustomerEmail: boolean;
    retryingCouponPreparation: boolean;
    savingAssignment: boolean;
    savingDecision: boolean;
    savingCustomerNote: boolean;
    savingInternalNote: boolean;
    sendingConversationMessage: boolean;
    uploadingAttachment: boolean;
    removingAttachmentId?: string;
    downloadingAttachmentId?: string;
    selectingEvidenceId?: string;
    mutationMessage?: string;
    nextCursor: string | null;
    service?: MerchantRequestsService;
    summary: RequestQueueSummary;
  } => ({
    assignmentDrafts: {},
    decisionDrafts: {},
    escalatingOverdue: false,
    filters: defaultRequestFilters(),
    items: [],
    loadingDetail: false,
    loadingList: false,
    nextCursor: null,
    refreshingEvidence: false,
    resendingCustomerEmail: false,
    retryingCouponPreparation: false,
    savingAssignment: false,
    savingDecision: false,
    savingCustomerNote: false,
    loadingCustomerLinkCandidates: false,
    savingInternalNote: false,
    sendingConversationMessage: false,
    uploadingAttachment: false,
    summary: emptyRequestSummary(),
  }),
  actions: {
    configure(service: MerchantRequestsService): void {
      this.service = service;
    },
    async loadList(filters: RequestQueueFilters): Promise<void> {
      this.filters = filters;
      this.loadingList = true;
      delete this.listError;
      try {
        const page = await this.requireService().loadRequests(filters);
        this.items = page.items;
        this.nextCursor = page.nextCursor;
        this.summary = page.summary;
      } catch (error: unknown) {
        this.listError = messageForError(error, "Request queue could not be loaded.");
        this.items = [];
        this.nextCursor = null;
      } finally {
        this.loadingList = false;
      }
    },
    async loadMore(): Promise<void> {
      if (this.nextCursor === null) return;
      this.loadingList = true;
      delete this.listError;
      try {
        const page = await this.requireService().loadRequests({
          ...this.filters,
          cursor: this.nextCursor,
        });
        this.items = [...this.items, ...page.items];
        this.nextCursor = page.nextCursor;
        this.summary = page.summary;
      } catch (error: unknown) {
        this.listError = messageForError(error, "More requests could not be loaded.");
      } finally {
        this.loadingList = false;
      }
    },
    async loadDetail(requestId: string): Promise<void> {
      this.loadingDetail = true;
      delete this.detailError;
      delete this.customerNoteError;
      delete this.customerLinkError;
      delete this.customerLinkCandidates;
      delete this.customerLinkMessage;
      delete this.conversationError;
      delete this.internalNoteError;
      delete this.attachmentError;
      delete this.mutationError;
      try {
        this.detail = await this.requireService().loadRequest(requestId);
        this.ensureDrafts(requestId);
      } catch (error: unknown) {
        this.detailError = messageForError(error, "Request detail could not be loaded.");
        delete this.detail;
      } finally {
        this.loadingDetail = false;
      }
    },
    ensureDrafts(requestId: string): void {
      if (this.detail?.requestId === requestId) {
        this.decisionDrafts[requestId] = decisionDraftFromLoadedDetail(this.detail);
        this.assignmentDrafts[requestId] ??= assignmentDraftFromLoadedDetail(this.detail);
      } else {
        this.decisionDrafts[requestId] ??= defaultDecisionDraft();
        this.assignmentDrafts[requestId] ??= defaultAssignmentDraft();
      }
    },
    async submitDecision(requestId: string): Promise<void> {
      if (this.detail === undefined) return;
      const draft = this.decisionDrafts[requestId] ?? defaultDecisionDraft();
      this.savingDecision = true;
      try {
        await this.runMutation(
          () => this.requireService().submitDecision(requestId, draft),
          decisionSuccessMessage(draft.action),
        );
      } finally {
        this.savingDecision = false;
      }
    },
    async assignRequest(requestId: string): Promise<boolean> {
      if (this.detail === undefined) return false;
      this.savingAssignment = true;
      delete this.assignmentError;
      delete this.mutationError;
      delete this.mutationMessage;
      try {
        this.detail = await this.requireService().assignRequest(
          requestId,
          this.assignmentDrafts[requestId] ?? defaultAssignmentDraft(),
        );
        this.mutationMessage = "Assignment saved.";
        return true;
      } catch (error: unknown) {
        this.assignmentError = messageForError(error, "Assignment could not be saved.");
        return false;
      } finally {
        this.savingAssignment = false;
      }
    },
    async retryEvidence(requestId: string): Promise<void> {
      this.refreshingEvidence = true;
      delete this.mutationError;
      delete this.mutationMessage;
      try {
        this.detail = await this.requireService().retryEvidence(requestId);
        this.mutationMessage = "Evidence refreshed.";
      } catch (error: unknown) {
        this.mutationError = messageForError(error, "Evidence refresh could not be completed.");
      } finally {
        this.refreshingEvidence = false;
      }
    },
    async resendCustomerEmail(requestId: string): Promise<void> {
      this.resendingCustomerEmail = true;
      delete this.mutationError;
      delete this.mutationMessage;
      try {
        this.detail = await this.requireService().resendCustomerEmail(requestId);
        this.mutationMessage = this.detail.customerEmailDeliveryFailed
          ? "The status email could not be sent. You can try again."
          : "The latest request status email was sent.";
      } catch (error: unknown) {
        this.mutationError = messageForError(error, "Customer status email could not be sent.");
      } finally {
        this.resendingCustomerEmail = false;
      }
    },
    async retryCouponPreparation(requestId: string): Promise<void> {
      this.retryingCouponPreparation = true;
      delete this.mutationError;
      delete this.mutationMessage;
      try {
        this.detail = await this.requireService().retryCouponPreparation(requestId);
        this.mutationMessage =
          this.detail.offer?.status === OFFER_STATUS_ACCEPTED
            ? "Coupon created and the shopper's pending acceptance was completed."
            : "Coupon retry finished. Refresh the request to see its current state.";
      } catch (error: unknown) {
        this.mutationError = messageForError(error, "Coupon retry could not be completed.");
      } finally {
        this.retryingCouponPreparation = false;
      }
    },
    async createCustomerNote(requestId: string, body: string): Promise<void> {
      this.savingCustomerNote = true;
      delete this.customerNoteError;
      delete this.mutationMessage;
      try {
        this.applyCustomerNoteDetail(
          await this.requireService().createCustomerNote(requestId, { body }),
        );
        this.mutationMessage = "Customer note saved.";
      } catch (error: unknown) {
        this.customerNoteError = messageForError(error, "Customer note could not be saved.");
      } finally {
        this.savingCustomerNote = false;
      }
    },
    async loadCustomerLinkCandidates(requestId: string): Promise<void> {
      this.loadingCustomerLinkCandidates = true;
      delete this.customerLinkError;
      delete this.customerLinkCandidates;
      delete this.customerLinkMessage;
      try {
        this.customerLinkCandidates =
          await this.requireService().loadCustomerLinkCandidates(requestId);
      } catch (error: unknown) {
        this.customerLinkError = messageForError(
          error,
          "Matching customer profiles could not be loaded. Try again.",
        );
      } finally {
        this.loadingCustomerLinkCandidates = false;
      }
    },
    async linkGuestRequestCustomer(requestId: string, customerId: string): Promise<void> {
      this.linkingCustomerId = customerId;
      delete this.customerLinkError;
      delete this.mutationMessage;
      delete this.customerLinkMessage;
      try {
        this.detail = await this.requireService().linkGuestRequestCustomer(requestId, customerId);
        delete this.customerLinkCandidates;
        this.customerLinkMessage = "Guest request linked to the selected customer.";
      } catch (error: unknown) {
        this.customerLinkError = messageForError(
          error,
          "The customer link could not be completed. Refresh the request and try again.",
        );
      } finally {
        delete this.linkingCustomerId;
      }
    },
    async updateCustomerNote(requestId: string, noteId: string, body: string): Promise<void> {
      this.savingCustomerNote = true;
      delete this.customerNoteError;
      delete this.mutationMessage;
      try {
        this.applyCustomerNoteDetail(
          await this.requireService().updateCustomerNote(requestId, noteId, {
            body,
          }),
        );
        this.mutationMessage = "Customer note updated.";
      } catch (error: unknown) {
        this.customerNoteError = messageForError(error, "Customer note could not be updated.");
      } finally {
        this.savingCustomerNote = false;
      }
    },
    async createInternalNote(requestId: string, body: string): Promise<void> {
      this.savingInternalNote = true;
      delete this.internalNoteError;
      delete this.mutationMessage;
      try {
        this.applyInternalNoteDetail(
          await this.requireService().createInternalNote(requestId, { body }),
        );
        this.mutationMessage = "Internal note saved.";
      } catch (error: unknown) {
        this.internalNoteError = messageForError(error, "Internal note could not be saved.");
      } finally {
        this.savingInternalNote = false;
      }
    },
    async createConversationMessage(requestId: string, body: string): Promise<void> {
      this.sendingConversationMessage = true;
      delete this.conversationError;
      delete this.mutationMessage;
      try {
        this.applyConversationDetail(
          await this.requireService().createConversationMessage(requestId, { body }),
        );
        this.mutationMessage = "Message sent.";
      } catch (error: unknown) {
        this.conversationError = messageForError(error, "Message could not be sent.");
      } finally {
        this.sendingConversationMessage = false;
      }
    },
    async uploadAttachment(requestId: string, file: File): Promise<void> {
      this.uploadingAttachment = true;
      delete this.attachmentError;
      delete this.mutationMessage;
      try {
        this.applyAttachmentDetail(await this.requireService().uploadAttachment(requestId, file));
        this.mutationMessage = "Attachment uploaded.";
      } catch (error: unknown) {
        this.attachmentError = messageForError(error, "Attachment could not be uploaded.");
      } finally {
        this.uploadingAttachment = false;
      }
    },
    async downloadAttachment(requestId: string, attachmentId: string): Promise<string | undefined> {
      this.downloadingAttachmentId = attachmentId;
      delete this.attachmentError;
      try {
        return await this.requireService().downloadAttachment(requestId, attachmentId);
      } catch (error: unknown) {
        this.attachmentError = messageForError(error, "Attachment could not be downloaded.");
        return undefined;
      } finally {
        delete this.downloadingAttachmentId;
      }
    },
    async removeAttachment(requestId: string, attachmentId: string): Promise<void> {
      this.removingAttachmentId = attachmentId;
      delete this.attachmentError;
      delete this.mutationMessage;
      try {
        this.applyAttachmentDetail(
          await this.requireService().removeAttachment(requestId, attachmentId),
        );
        this.mutationMessage = "Attachment removed.";
      } catch (error: unknown) {
        this.attachmentError = messageForError(error, "Attachment could not be removed.");
      } finally {
        delete this.removingAttachmentId;
      }
    },
    async updateInternalNote(requestId: string, noteId: string, body: string): Promise<void> {
      this.savingInternalNote = true;
      delete this.internalNoteError;
      delete this.mutationMessage;
      try {
        this.applyInternalNoteDetail(
          await this.requireService().updateInternalNote(requestId, noteId, {
            body,
          }),
        );
        this.mutationMessage = "Internal note updated.";
      } catch (error: unknown) {
        this.internalNoteError = messageForError(error, "Internal note could not be updated.");
      } finally {
        this.savingInternalNote = false;
      }
    },
    async selectEvidence(requestId: string, snapshotId: string): Promise<void> {
      if (this.detail === undefined) return;
      this.selectingEvidenceId = snapshotId;
      delete this.mutationError;
      delete this.mutationMessage;
      try {
        this.detail = await this.requireService().selectEvidence(requestId, snapshotId);
        this.mutationMessage = "Evidence selected.";
      } catch (error: unknown) {
        this.mutationError = messageForError(error, "Evidence selection could not be saved.");
      } finally {
        delete this.selectingEvidenceId;
      }
    },
    async escalateOverdueRequests(): Promise<void> {
      this.escalatingOverdue = true;
      delete this.listError;
      try {
        const result = await this.requireService().escalateOverdueRequests();
        this.mutationMessage = result.message;
        await this.loadList({ ...this.filters, cursor: undefined });
      } catch (error: unknown) {
        this.listError = messageForError(error, "Overdue requests could not be escalated.");
      } finally {
        this.escalatingOverdue = false;
      }
    },
    async runMutation(
      operation: () => Promise<PriceMatchRequestDetail>,
      successMessage: string,
    ): Promise<void> {
      delete this.mutationError;
      delete this.mutationMessage;
      try {
        this.detail = await operation();
        this.mutationMessage = successMessage;
      } catch (error: unknown) {
        this.mutationError = messageForError(error, "Request update could not be saved.");
      }
    },
    applyCustomerNoteDetail(updatedDetail: PriceMatchRequestDetail): void {
      this.applyDetailSections(updatedDetail, ["customerContact"]);
    },
    applyInternalNoteDetail(updatedDetail: PriceMatchRequestDetail): void {
      this.applyDetailSections(updatedDetail, ["internalNotes"]);
    },
    applyConversationDetail(updatedDetail: PriceMatchRequestDetail): void {
      this.applyDetailSections(updatedDetail, ["conversation"]);
    },
    applyAttachmentDetail(updatedDetail: PriceMatchRequestDetail): void {
      this.applyDetailSections(updatedDetail, ["attachments"]);
    },
    applyDetailSections(
      updatedDetail: PriceMatchRequestDetail,
      sections: DetailSectionName[],
    ): void {
      if (this.detail === undefined) {
        this.detail = updatedDetail;
        return;
      }

      const nextDetail: PriceMatchRequestDetail = {
        ...this.detail,
        timeline: updatedDetail.timeline,
      };

      for (const section of sections) {
        copyDetailSection(nextDetail, updatedDetail, section);
      }

      this.detail = nextDetail;
    },
    requireService(): MerchantRequestsService {
      if (this.service === undefined) {
        throw new Error("Requests store was not configured before use.");
      }
      return this.service;
    },
  },
});

type DetailSectionName = "attachments" | "conversation" | "customerContact" | "internalNotes";

function copyDetailSection(
  target: PriceMatchRequestDetail,
  source: PriceMatchRequestDetail,
  section: DetailSectionName,
): void {
  if (section === "attachments") target.attachments = source.attachments;
  if (section === "conversation") target.conversation = source.conversation;
  if (section === "customerContact") target.customerContact = source.customerContact;
  if (section === "internalNotes") target.internalNotes = source.internalNotes;
}

export function defaultRequestFilters(): RequestQueueFilters {
  return {
    limit: 25,
    sort: "newest",
  };
}

export function emptyRequestSummary(): RequestQueueSummary {
  return {
    allCount: 0,
    assignedToMeCount: 0,
    dueSoonCount: 0,
    overdueCount: 0,
    reviewQueueCount: 0,
  };
}

export function defaultDecisionDraft(): DecisionDraft {
  return {
    action: "APPROVE",
    reasonCode: "",
    reason: "",
    customerMessage: "",
    internalNote: "",
  };
}

function decisionDraftFromLoadedDetail(detail: PriceMatchRequestDetail): DecisionDraft {
  const action = decisionActionFromLoadedDetail(detail);
  const draft: DecisionDraft = {
    action,
    reasonCode: detail.policyResult.decisionReasonCode ?? "",
    reason: detail.policyResult.decisionReasonLabel ?? detail.policyResult.reason ?? "",
    customerMessage: "",
    internalNote: "",
  };

  const counterOfferPriceMinor =
    detail.pricingDecision.proposedOfferPriceMinor ?? detail.offer?.offeredPriceMinor;
  if (action === "COUNTER" && counterOfferPriceMinor !== undefined) {
    draft.counterOfferPriceMinor = counterOfferPriceMinor;
  }

  return draft;
}

function decisionActionFromLoadedDetail(detail: PriceMatchRequestDetail): RequestDecisionAction {
  const actionFromReason = detail.decision.reasonOptions.find(
    (option) => option.code === detail.policyResult.decisionReasonCode,
  )?.action;
  if (isVisibleDecisionAction(actionFromReason)) return actionFromReason;

  if (detail.outcome === "COUNTEROFFER") return "COUNTER";
  if (detail.outcome === "NO_OFFER") return "REJECT";
  return "APPROVE";
}

function isVisibleDecisionAction(
  action: RequestDecisionAction | undefined,
): action is RequestDecisionAction {
  return action === "APPROVE" || action === "COUNTER" || action === "REJECT";
}

export function defaultAssignmentDraft(): AssignmentDraft {
  return {
    assigneeUserId: "",
  };
}

function assignmentDraftFromLoadedDetail(detail: PriceMatchRequestDetail): AssignmentDraft {
  return {
    assigneeUserId: detail.assignment.assigneeUserId ?? "",
  };
}

function messageForError(error: unknown, fallback: string): string {
  if (error instanceof Error) return error.message;
  return fallback;
}

function decisionSuccessMessage(action: DecisionDraft["action"]): string {
  if (action === "APPROVE") return "Request approved.";
  if (action === "COUNTER") return "Counteroffer sent.";
  if (action === "REJECT") return "Request rejected.";
  if (action === "REQUEST_INFO") return "Customer information request sent.";
  return "Request marked unverifiable.";
}
